import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { fetchStats } from "../lib/api";

export function ProfilePage() {
	const { user } = useAuth();
	const { data: stats } = useQuery({
		queryKey: ["stats"],
		queryFn: fetchStats,
	});

	return (
		<div className="px-4 md:px-0">
			<header className="mb-6 md:mb-8">
				<h1 className="font-[Space_Grotesk] text-[28px] md:text-[48px] leading-[1.1] tracking-[-0.04em] font-bold text-on-surface">
					MY PROFILE
				</h1>
			</header>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
				<div className="bg-brand-white border-4 border-brand-black p-6 md:p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
					<div className="flex items-start gap-4 md:gap-6 mb-6">
						<div className="w-16 h-16 md:w-20 md:h-20 rounded-full border-[3px] border-brand-black bg-secondary-container flex items-center justify-center flex-shrink-0 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
							<span
								className="material-symbols-outlined text-[32px] md:text-[40px] text-brand-black"
								style={{ fontVariationSettings: "'FILL' 1" }}
							>
								person
							</span>
						</div>
						<div className="pt-1 min-w-0">
							<h2 className="font-headline-md text-[20px] md:text-[24px] leading-[1.2] font-bold font-[Space_Grotesk] uppercase text-on-surface break-all">
								{user?.user_metadata?.full_name ||
									user?.user_metadata?.name ||
									"Caffeine Fiend"}
							</h2>
							<p className="font-body-md text-[14px] md:text-[16px] leading-[1.5] text-outline break-all">
								{user?.email}
							</p>
						</div>
					</div>

					<div className="border-t-[3px] border-brand-black pt-4 space-y-3">
						<div className="flex justify-between items-center">
							<span className="font-label-bold text-[12px] md:text-[14px] leading-none font-bold font-[Space_Grotesk] text-outline uppercase">
								Auth Provider
							</span>
							<span className="font-body-md text-[14px] md:text-[16px] leading-[1.5] text-on-surface font-medium capitalize">
								{user?.app_metadata?.provider || "google"}
							</span>
						</div>
						<div className="flex justify-between items-center">
							<span className="font-label-bold text-[12px] md:text-[14px] leading-none font-bold font-[Space_Grotesk] text-outline uppercase">
								Joined
							</span>
							<span className="font-body-md text-[14px] md:text-[16px] leading-[1.5] text-on-surface font-medium">
								{user?.created_at
									? new Date(user.created_at).toLocaleDateString("en-US", {
											year: "numeric",
											month: "long",
											day: "numeric",
										})
									: "--"}
							</span>
						</div>
					</div>
				</div>

				<div className="bg-brand-white border-4 border-brand-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
					<div className="p-4 md:p-6 border-b-4 border-brand-black bg-surface-container-highest">
						<h2 className="font-headline-md text-[18px] md:text-[24px] leading-[1.2] font-bold font-[Space_Grotesk] text-on-surface uppercase">
							Drink Stats
						</h2>
					</div>

					{!stats || stats.total_ratings === 0 ? (
						<div className="p-6 md:p-8 text-center">
							<span
								className="material-symbols-outlined text-[48px] text-outline mb-3 block"
								style={{ fontVariationSettings: "'FILL' 1" }}
							>
								local_cafe
							</span>
							<p className="font-body-md text-[14px] md:text-[16px] leading-[1.5] text-outline">
								No ratings yet. Log your first drink!
							</p>
						</div>
					) : (
						<div className="p-4 md:p-6 space-y-4">
							<div className="flex justify-between items-center py-2 border-b-2 border-brand-black">
								<span className="font-label-bold text-[12px] md:text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase">
									Total Rated
								</span>
								<span className="font-headline-md text-[18px] md:text-[24px] leading-[1.2] font-bold font-[Space_Grotesk] text-brand-blue">
									{stats.total_ratings}
								</span>
							</div>
							<div className="flex justify-between items-center py-2 border-b-2 border-brand-black">
								<span className="font-label-bold text-[12px] md:text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase">
									Avg Flavor
								</span>
								<span className="font-headline-md text-[18px] md:text-[24px] leading-[1.2] font-bold font-[Space_Grotesk] text-brand-blue">
									{stats.average_flavor_score} / 5
								</span>
							</div>
							<div className="flex justify-between items-center py-2 border-b-2 border-brand-black">
								<span className="font-label-bold text-[12px] md:text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase">
									Avg Energy
								</span>
								<span className="font-headline-md text-[18px] md:text-[24px] leading-[1.2] font-bold font-[Space_Grotesk] text-brand-green">
									{stats.average_energy_level} / 10
								</span>
							</div>

							{stats.categories && (
								<div className="py-2">
									<span className="font-label-bold text-[12px] md:text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase block mb-3">
										Categories
									</span>
									<div className="flex gap-2">
										{(["energy", "soda", "sparkling"] as const).map((cat) => (
											<div
												key={cat}
												className={`flex-1 px-3 py-2 border-[3px] border-brand-black text-center ${
													cat === "energy"
														? "bg-brand-blue text-brand-white"
														: cat === "soda"
															? "bg-brand-orange text-brand-white"
															: "bg-brand-green text-brand-black"
												}`}
											>
												<div className="font-headline-md text-[18px] md:text-[24px] leading-[1.2] font-bold font-[Space_Grotesk]">
													{stats.categories![cat]}
												</div>
												<div className="font-label-bold text-[10px] md:text-[12px] leading-none font-bold font-[Space_Grotesk] uppercase">
													{cat}
												</div>
											</div>
										))}
									</div>
								</div>
							)}

							{stats.top_vibe_tags.length > 0 && (
								<div className="pt-2">
									<span className="font-label-bold text-[12px] md:text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase block mb-2">
										Top Vibe Tags
									</span>
									<div className="flex flex-wrap gap-1.5">
										{stats.top_vibe_tags.map((tag) => (
											<span
												key={tag}
												className="px-2.5 py-1 border-2 border-brand-black bg-secondary-container text-brand-black font-label-bold text-[11px] md:text-[12px] leading-none font-bold font-[Space_Grotesk] uppercase"
											>
												{tag}
											</span>
										))}
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
